import { html } from "../utils/dom-utils";
import { Option } from "../utils/validation";
import { DataFetch } from "./base-component";
import { OptionFieldValidator } from "../utils/option-field-validator";
import {
  OptionFieldManager,
  TypeaheadFieldManager,
} from "../utils/option-field-manager";

export function updateTypeaheadDisabledState(
  dropdown: HTMLElement,
  isDisabled: boolean,
  customTooltip?: string,
): void {
  const tooltip = customTooltip || (isDisabled ? "Select a Program Area" : "");

  const hiddenInput = dropdown.querySelector(
    'input[type="hidden"]',
  ) as HTMLInputElement;
  const visibleInput = dropdown.querySelector(
    'input[type="text"]',
  ) as HTMLInputElement;

  if (!hiddenInput || !visibleInput)
    throw new Error("Not a typeahead being disabled");

  hiddenInput.disabled = isDisabled;
  visibleInput.disabled = isDisabled;

  const dropdownButton = dropdown.querySelector(
    'button[id$="-dropdown-btn"]',
  ) as HTMLButtonElement;
  if (dropdownButton) {
    dropdownButton.disabled = isDisabled;
  }

  const parent = hiddenInput.parentElement!;

  if (isDisabled) {
    visibleInput.setAttribute("title", tooltip);
    parent.classList.add("dxbl-disabled");
  } else {
    visibleInput.removeAttribute("title");
    parent.classList.remove("dxbl-disabled");
  }
}

export function setupTypeahead(
  input: HTMLInputElement,
  shownInput: HTMLInputElement,
  debounce: number | null,
  dataFetch: DataFetch,
  displayProperty: string,
  valueProperty: string,
  initialAvailableOptions: Option[] = [],
) {
  let availableOptions: Option[] = initialAvailableOptions;

  const fieldGroup = input.closest("[data-option-field-group]") as HTMLElement;
  if (!fieldGroup || input.hasAttribute("data-typeahead-validated")) {
    return;
  }

  const manager = new TypeaheadFieldManager(fieldGroup, input, shownInput);
  const validator = new OptionFieldValidator(
    () => availableOptions,
    () => input.value,
    false,
  );
  const fieldName = shownInput.getAttribute("data-field-name") || "Field";

  manager.setupValidation(validator, [], fieldName);

  input.setAttribute("data-typeahead-validated", "true");
  const validationSpan = document.querySelector(
    `[data-validation-for="${input.id}"]`,
  );
  if (validationSpan) {
    (validationSpan as any)._isTypeaheadManaged = true;
  }

  // Now check for dropdown button - if missing, validation still works but dropdown won't
  const dropdownButton = document.getElementById(`${input.id}-dropdown-btn`);
  if (dropdownButton == null) {
    console.error(
      "Dropdown button is missing for " +
        input.id +
        " - validation will work but dropdown will not",
    );
    return;
  }

  if (input.value == null) return;

  // Only set initial display value if there's actually a value selected
  if (input.value !== "" && input.value !== "0") {
    if (shownInput.value.trim() === "") {
      if (initialAvailableOptions.length > 0) {
        const selectedOption = initialAvailableOptions.find(
          (o) => o.value === input.value,
        );
        if (selectedOption) {
          shownInput.value = selectedOption.display;
          availableOptions = initialAvailableOptions;
        } else {
          setInitialValue();
        }
      } else {
        setInitialValue();
      }
    } else {
      const preselectedOption = {
        value: input.value,
        display: shownInput.value.trim(),
      };
      availableOptions = [preselectedOption];

      setTimeout(() => {
        const changeEvent = new Event("change", { bubbles: true });
        input.dispatchEvent(changeEvent);
      }, 0);
    }
  }

  const suggestionsId = `${input.id}-suggestions`;
  const newSuggestionBuilder = () => {
    const suggestions = html`
    <div class="dxbl-dropdown" id="${suggestionsId}-box" style="position: absolute; z-index: 1000; display: none;"
         role="listbox" aria-label="Suggestions">
      <div class="dxbl-dropdown-dialog dxbl-edit-dropdown dxbl-edit-dropdown">
        <div class="dxbl-dropdown-body dxbl-edit-dropdown-body">
          <div class="dxbl-listbox">
            <ul id=${suggestionsId} class="dxbs-dm" />
          </div>
        </div>
      </div>
    </div>
    `;
    if (suggestions instanceof Array) throw Error("Should only get one el");
    document.body.appendChild(suggestions);
    return document.getElementById(suggestionsId)!;
  };
  const suggestions =
    document.getElementById(suggestionsId) ?? newSuggestionBuilder();
  const suggestionBox = document.getElementById(suggestionsId + "-box");

  // SAFETY CHECK: If DOM creation fails in tests, still allow validation to work
  if (!suggestions || !suggestionBox) {
    console.warn(
      `Typeahead DOM setup failed for ${input.id} - validation works but dropdown disabled`,
    );
    return; // Exit early but validation is already set up above
  }

  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", suggestionsId);
  input.setAttribute("aria-expanded", "false");

  let currentSuggestionIndex = -1;
  let suggestionItems: HTMLElement[] = [];
  let debounceTimer: number | null = null;

  const cleanupEventListeners = () => {
    document.removeEventListener("click", handleOutsideClick);
  };

  const handleInput = () => {
    if (debounceTimer) {
      window.clearTimeout(debounceTimer);
    }
    debounceTimer = window.setTimeout(() => {
      fetchAndShowSuggestions();
    }, debounce ?? 0);
  };

  shownInput.addEventListener("input", (e) => {
    if (e.target != document.activeElement) return;

    if (shownInput.value.trim() === "") {
      input.value = "";
      const event = new Event("change", { bubbles: true });
      input.dispatchEvent(event);
    }

    handleInput();
  });

  dropdownButton.addEventListener("click", (e) => {
    e.stopPropagation();
    if (suggestionBox && suggestionBox.style.display === "block") {
      hideSuggestions();
    } else {
      fetchAndShowSuggestions();
    }
    shownInput.focus();
  });

  shownInput.addEventListener("focus", () => {
    fetchAndShowSuggestions();
    shownInput.select();
  });

  shownInput.addEventListener("blur", (e) => {
    // Use setTimeout to allow click events on dropdown to fire first
    setTimeout(() => {
      // Only attempt auto-selection if the dropdown is not visible
      // If dropdown is visible, user is interacting with it, so don't interfere
      if (!suggestionBox || suggestionBox.style.display !== "block") {
        attemptAutoSelection();
      }
    }, 200);
  });

  shownInput.addEventListener("keydown", function (e) {
    if (!suggestionBox || suggestionBox.style.display !== "block") return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        navigateSuggestions(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        navigateSuggestions(-1);
        break;
      case "Enter":
        e.preventDefault();
        if (
          currentSuggestionIndex >= 0 &&
          suggestionItems[currentSuggestionIndex]
        ) {
          selectSuggestion(suggestionItems[currentSuggestionIndex]);
        } else if (
          // if there is only one unambiguous match, just select it
          suggestionItems &&
          suggestionItems.length == 1
        ) {
          selectSuggestion(suggestionItems[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        hideSuggestions();
        shownInput.focus();
        break;
      case "Tab":
        attemptAutoSelection();
        hideSuggestions();
        break;
      default: // when typing, the hidden input shouldn't have a value
        input.value = "";
    }
  });

  async function setInitialValue() {
    try {
      input.classList.add("loading");
      dataFetch
        .fetchSuggestions(
          shownInput.value.trim(),
          displayProperty,
          valueProperty,
        )
        .then((results) => {
          if (results == null || results.length == 0) {
            console.error("Could not set typeahead value");
            return;
          }

          availableOptions = results;

          const selectedOption = results.find((o) => o.value == input.value);
          if (selectedOption == null && !!input.value && input.value !== "0") {
            console.error("A set typeahead value was not found:", input.value);
            return;
          } else if (selectedOption != null) {
            shownInput.value = selectedOption.display;
          }
        });
    } catch (error) {
      console.error("Error fetching typeahead value:", error);
    } finally {
      input.classList.remove("loading");
    }
  }

  async function fetchAndShowSuggestions() {
    try {
      // if an option is selected, search the default value to repopulate the list
      const searchValue = input.value ? "" : shownInput.value.trim();

      input.classList.add("loading");
      dataFetch
        .fetchSuggestions(searchValue, displayProperty, valueProperty)
        .then((results) => {
          showSuggestions(results ?? []);
        });
    } catch (error) {
      console.error("Error fetching typeahead suggestions:", error);
      hideSuggestions();
    } finally {
      input.classList.remove("loading");
    }
  }

  suggestions.addEventListener("click", function (e) {
    if (
      e.target &&
      e.target instanceof HTMLElement &&
      e.target.matches("div.dxbl-listbox-item")
    ) {
      selectSuggestion(e.target);
    }
  });

  function selectSuggestion(element: HTMLElement) {
    const value = element.getAttribute("data-sync-option-value");
    if (!value) return;
    input.value = value;
    shownInput.value = element.innerText;
    hideSuggestions();

    const event = new Event("change", { bubbles: true });
    input.dispatchEvent(event);

    const inputEvent = new Event("input", { bubbles: true });
    input.dispatchEvent(inputEvent);
  }

  function attemptAutoSelection(): boolean {
    const typedText = shownInput.value.trim();

    // If field is empty, nothing to auto-select
    if (typedText === "") return false;

    const exactMatches = availableOptions.filter((option) => {
      return option.display.toLowerCase() === typedText.toLowerCase();
    });

    // Only auto-select if there's exactly one match
    if (exactMatches.length !== 1) return false;

    const matchedOption = exactMatches[0];

    // Don't update if the value is already correct
    if (input.value === matchedOption.value) {
      return false;
    }

    input.value = matchedOption.value;
    shownInput.value = matchedOption.display;

    const event = new Event("change", { bubbles: true });
    input.dispatchEvent(event);

    const inputEvent = new Event("input", { bubbles: true });
    input.dispatchEvent(inputEvent);

    return true;
  }

  const handleOutsideClick = function (e: MouseEvent) {
    const target = e.target as Node;
    if (
      target !== shownInput &&
      target !== dropdownButton &&
      suggestionBox &&
      !suggestionBox.contains(target)
    ) {
      attemptAutoSelection();
      hideSuggestions();
    }
  };

  document.addEventListener("click", handleOutsideClick);

  function navigateSuggestions(direction: number) {
    if (suggestionItems.length === 0) return;

    if (
      currentSuggestionIndex >= 0 &&
      suggestionItems[currentSuggestionIndex]
    ) {
      suggestionItems[currentSuggestionIndex].classList.remove(
        "dxbl-listbox-item-selected",
      );
      suggestionItems[currentSuggestionIndex].setAttribute(
        "aria-selected",
        "false",
      );
    }

    currentSuggestionIndex += direction;

    if (currentSuggestionIndex < 0) {
      currentSuggestionIndex = suggestionItems.length - 1;
    } else if (currentSuggestionIndex >= suggestionItems.length) {
      currentSuggestionIndex = 0;
    }

    const selectedItem = suggestionItems[currentSuggestionIndex];
    selectedItem.classList.add("dxbl-listbox-item-selected");
    selectedItem.setAttribute("aria-selected", "true");

    input.setAttribute("aria-activedescendant", selectedItem.id);

    if (selectedItem.scrollIntoView) {
      selectedItem.scrollIntoView({ block: "nearest" });
    }
  }

  function showSuggestions(items: Option[]) {
    availableOptions = items;

    suggestions.innerHTML = "";
    currentSuggestionIndex = -1;
    suggestionItems = [];

    if (items.length > 0) {
      items.forEach((item, index) => {
        const itemId = `${suggestionsId}-item-${index}`;
        const listItem = html`
        <li>
          <div id="${itemId}" class="dxbl-listbox-item" role="option" aria-selected="false" data-sync-option-value="${item.value}">
            ${item.display}
          </div>
        </li>
        `;
        if (listItem instanceof Array) throw Error("Should only get one ul");
        suggestions.appendChild(listItem);

        suggestionItems.push(
          listItem.querySelector("div.dxbl-listbox-item") as HTMLElement,
        );
      });

      positionSuggestionBox();

      input.setAttribute("aria-expanded", "true");
    } else {
      const noResults = html`<li><div class="dxbl-listbox-item dxbl-listbox-item-disabled">No results found</div></li>`;
      suggestions.appendChild(noResults as HTMLElement);
      positionSuggestionBox();
    }
  }

  function positionSuggestionBox() {
    if (!suggestionBox) return; // Safety check

    const inputContainer = document.getElementById(`${input.id}-container`)!;
    const textEditDiv = inputContainer.querySelector(".dxbl-text-edit")!;
    const rect = textEditDiv.getBoundingClientRect();

    suggestionBox.style.position = "absolute";
    suggestionBox.style.top = `${rect.bottom + window.scrollY}px`;
    suggestionBox.style.left = `${rect.left + window.scrollX}px`;
    suggestionBox.style.width = `${rect.width}px`;
    suggestionBox.style.zIndex = "1000";
    suggestionBox.style.transform = "none";
    suggestionBox.style.display = "block";
  }

  function hideSuggestions() {
    if (suggestionBox) {
      suggestionBox.style.display = "none";
    }
    currentSuggestionIndex = -1;
    suggestionItems = [];
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        for (const node of Array.from(mutation.removedNodes)) {
          if (node.contains && (node.contains(input) || node === input)) {
            cleanupEventListeners();
            observer.disconnect();
            return;
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// always a string, since it's an input value
// type DataFetch = (value: string) => void;

export class AsyncTypeaheadDataFetch implements DataFetch {
  constructor(
    private apiEndpoint: string,
    private callback: (options: Option[]) => void,
  ) {}

  private onFetch: ((opt: Promise<Option[]>) => void) | null = null;

  private previousFetches: Promise<Option[]>[] = [];

  public async fetchSuggestions(
    value: string,
    displayProperty: string,
    valueProperty: string,
  ): Promise<Option[]> {
    for (const previous of this.previousFetches) {
      // Need to cancel all previous outstanding requests
      Promise.all([previous, Promise.reject()]);
    }
    const initialRequest = fetch(
      `${this.apiEndpoint}/${encodeURIComponent(value)}`,
    );
    const request: Promise<Option[]> = initialRequest.then((response) => {
      if (!response.ok)
        throw new Error(`Network response was not ok: ${response.status}`);

      return (response.json() as Promise<any[]>).then((opts) => {
        const options: Option[] = opts.map((o) => ({
          value: String(o[valueProperty] ?? ""),
          display: String(o[displayProperty] ?? ""),
        }));
        this.callback(options);
        return options;
      });
    });
    this.previousFetches.push(request);
    if (this.onFetch != null) {
      this.onFetch(request);
    }
    return request;
  }

  public registerOnFetch(onFetch: (opts: Promise<Option[]>) => void): void {
    this.onFetch = onFetch;
  }
}

export class SyncTypeaheadDataFetch implements DataFetch {
  constructor(private options: Option[]) {}

  public async fetchSuggestions(value: string): Promise<Option[]> {
    const optionPromise = Promise.resolve(
      this.options.filter((o) =>
        o.display.toLowerCase().includes(value.toLowerCase()),
      ),
    );
    if (this.onFetch) this.onFetch(optionPromise);
    return optionPromise;
  }
  private onFetch: ((opt: Promise<Option[]>) => void) | null = null;

  public registerOnFetch(onFetch: (opts: Promise<Option[]>) => void): void {
    this.onFetch = onFetch;
  }
}

export class GenericTypeahead {
  constructor(private debounce: number | null) {}

  private alreadyExistInputIds: string[] = [];

  private formOptionMap: { [key: string]: Option[] } = {};

  private dataFetchMap: {
    [key: string]: DataFetch;
  } = {};

  public init() {
    // Find all inputs with data-async-typeahead attribute
    document
      .querySelectorAll("input[data-async-typeahead]")
      .forEach((input) => {
        if (this.alreadyExistInputIds.includes(input.id)) return;
        const shownInput =
          input.parentElement?.getElementsByTagName("input")[0]!;

        const apiEndpoint = input.getAttribute("data-api-endpoint");
        const displayProperty = input.getAttribute("data-display-property");
        const valueProperty = input.getAttribute("data-value-property");

        if (apiEndpoint == null || displayProperty == null) {
          console.error(
            "Typeahead input is missing required data attributes",
            input,
          );
          return;
        }

        const dataFetch = new AsyncTypeaheadDataFetch(
          apiEndpoint,
          (options) => {
            // TODO: move this into promise?
            this.formOptionMap[input.id] = options;
          },
        );
        this.dataFetchMap[input.id] = dataFetch;

        this.alreadyExistInputIds.push(input.id);
        setupTypeahead(
          input as HTMLInputElement,
          shownInput,
          this.debounce,
          dataFetch,
          displayProperty!,
          valueProperty!,
          [], // Async typeahead will populate availableOptions when data is fetched
        );
      });
    document.querySelectorAll("input[data-sync-typeahead]").forEach((input) => {
      if (this.alreadyExistInputIds.includes(input.id)) return;
      const displayProperty = input.getAttribute("data-display-property");
      const shownInput = input.parentElement?.getElementsByTagName("input")[0]!;
      const valueProperty = input.getAttribute("data-value-property");

      // CRITICAL FIX: Set up fallback validation even when typeahead initialization fails
      // This ensures ValidSelect validation works even with malformed DOM or missing attributes
      const setupFallbackValidation = (options: Option[] = []) => {
        const fieldGroup = input.closest(
          "[data-option-field-group]",
        ) as HTMLElement;
        if (fieldGroup && !input.hasAttribute("data-typeahead-validated")) {
          const manager = new TypeaheadFieldManager(
            fieldGroup,
            input as HTMLInputElement,
            shownInput,
          );
          const validator = new OptionFieldValidator(
            () => options,
            () => (input as HTMLInputElement).value,
            false, // isMultiselect = false for typeaheads
          );
          const fieldName =
            shownInput.getAttribute("data-field-name") || "Field";
          manager.setupValidation(validator, [], fieldName);
          input.setAttribute("data-typeahead-validated", "true");
        }
      };

      if (displayProperty == null) {
        console.error(
          "Typeahead input is missing required data attributes: displayProperty",
          input,
        );
        setupFallbackValidation();
        return;
      }
      const optionContainer = input.parentElement?.querySelectorAll(
        "div[data-sync-typeahead-options]",
      );
      if (optionContainer == null || optionContainer.length != 1) {
        console.error(
          "Typeahead input is missing required data attributes: options",
          input,
        );
        setupFallbackValidation();
        return;
      }
      const options: Option[] = [];
      optionContainer[0]
        .querySelectorAll("div[data-sync-option-value]")
        .forEach((optElement) => {
          options.push({
            value: optElement.getAttribute("data-sync-option-value")!,
            display: optElement.innerHTML.trim(),
          });
        });
      this.formOptionMap[input.id] = options;
      optionContainer[0].classList.add("d-none");

      const inputValue = (input as HTMLInputElement).value;
      if (inputValue != null) {
        const foundOption = options.find((o) => o.value == inputValue);
        shownInput.value = foundOption?.display ?? "";
      }
      this.alreadyExistInputIds.push(input.id);
      setupTypeahead(
        input as HTMLInputElement,
        shownInput,
        null,
        new SyncTypeaheadDataFetch(options),
        // TODO: Remove display property for SyncTypeaheadOption
        // and set up property typing to disambiguate Sync from Async
        // Or actually use the displayProperty for Sync
        displayProperty,
        valueProperty!,
        options, // Pass available options for sync typeahead validation
      );
    });
  }

  public getFormWithOptions(name: string): Option[] {
    return this.formOptionMap[name] ?? [];
  }

  public getDataFetch(name: string): DataFetch {
    return this.dataFetchMap[name];
  }

  public resetInput(inputId: string) {
    this.alreadyExistInputIds = this.alreadyExistInputIds.filter(
      (id) => id != inputId,
    );

    // Also remove the data-typeahead-validated attribute if the element exists
    // This ensures setupTypeahead() will run again for dynamically recreated elements
    const input = document.getElementById(inputId);
    if (input) {
      input.removeAttribute("data-typeahead-validated");
    }
  }
}
